import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, Legend } from 'recharts'
import FranceMap from '../FranceMap.jsx'

const AURA_DEPS = ['01','03','07','15','26','38','42','43','63','69','73','74']

const DEP_TO_REGION = {
  '75':'IDF','77':'IDF','78':'IDF','91':'IDF','92':'IDF','93':'IDF','94':'IDF','95':'IDF',
  '01':'AURA','03':'AURA','07':'AURA','15':'AURA','26':'AURA','38':'AURA','42':'AURA','43':'AURA','63':'AURA','69':'AURA','73':'AURA','74':'AURA',
  '13':'PACA','83':'PACA','84':'PACA','04':'PACA','05':'PACA','06':'PACA',
  '34':'OCC','30':'OCC','11':'OCC','66':'OCC','09':'OCC','12':'OCC','31':'OCC','32':'OCC','46':'OCC','48':'OCC','65':'OCC','81':'OCC','82':'OCC',
  '33':'NAQ','17':'NAQ','16':'NAQ','24':'NAQ','19':'NAQ','23':'NAQ','87':'NAQ','40':'NAQ','47':'NAQ','64':'NAQ','79':'NAQ','86':'NAQ',
  '44':'PDL','49':'PDL','53':'PDL','72':'PDL','85':'PDL',
  '35':'BRE','22':'BRE','56':'BRE','29':'BRE',
  '76':'NOR','27':'NOR','14':'NOR','50':'NOR','61':'NOR',
  '59':'HDF','62':'HDF','02':'HDF','60':'HDF','80':'HDF',
  '67':'GES','68':'GES','57':'GES','54':'GES','55':'GES','88':'GES','08':'GES','10':'GES','51':'GES','52':'GES',
  '21':'BFC','71':'BFC','89':'BFC','58':'BFC','25':'BFC','39':'BFC','70':'BFC','90':'BFC',
  '37':'CVL','41':'CVL','18':'CVL','45':'CVL','28':'CVL','36':'CVL',
}

export default function Dashboard() {
  const [onglet, setOnglet] = useState('suivi')
  const [coureurs, setCoureurs] = useState([])
  const [courses, setCourses] = useState([])
  const [points, setPoints] = useState([])
  const [coursePoints, setCoursePoints] = useState([])
  const [detections, setDetections] = useState([])
  const [retraits, setRetraits] = useState([])
  const [loading, setLoading] = useState(true)

  const [cpRef, setCpRef] = useState(() => { try { return JSON.parse(localStorage.getItem('statsConfig') || '{}').cp || '73190' } catch { return '73190' } })
  const [depRef, setDepRef] = useState(() => { try { return JSON.parse(localStorage.getItem('statsConfig') || '{}').dep || '73' } catch { return '73' } })
  const [regionRef, setRegionRef] = useState(() => { try { return JSON.parse(localStorage.getItem('statsConfig') || '{}').region || 'Auvergne-Rhone-Alpes' } catch { return 'Auvergne-Rhone-Alpes' } })
  const [granularite, setGranularite] = useState('region')
  const [filterStatsCourse, setFilterStatsCourse] = useState('')
  const [fluxPoint, setFluxPoint] = useState(null)
  const [dateJ0, setDateJ0] = useState(() => { try { return JSON.parse(localStorage.getItem('statsConfig') || '{}').dateJ0 || '' } catch { return '' } })

  useEffect(() => {
    fetchAll()
    const sub = supabase.channel('dashboard-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'coureurs' }, fetchAll)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'detections' }, fetchAll)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'dossards_retraits' }, fetchAll)
      .subscribe()
    return () => supabase.removeChannel(sub)
  }, [])

  async function fetchAll() {
    const [c, co, p, cp, d, r] = await Promise.all([
      supabase.from('coureurs').select('*, courses(nom, lot_coureur, lot_tombola)'),
      supabase.from('courses').select('*').order('nom'),
      supabase.from('points_chrono').select('*').order('nom'),
      supabase.from('course_points').select('*').order('ordre'),
      supabase.from('detections').select('*'),
      supabase.from('dossards_retraits').select('*').eq('invalide', false),
    ])
    setCoureurs(c.data || [])
    setCourses(co.data || [])
    setPoints(p.data || [])
    setCoursePoints(cp.data || [])
    setDetections(d.data || [])
    setRetraits(r.data || [])
    setLoading(false)
  }

  function saveConfig() {
    localStorage.setItem('statsConfig', JSON.stringify({ cp: cpRef, dep: depRef, region: regionRef, dateJ0 }))
  }

  function getDetectionRetenue(coureurId, pointId) {
    const dets = detections.filter(d => d.coureur_id === coureurId && d.point_chrono_id === pointId)
    const forcee = dets.find(d => d.forcee && !d.invalide)
    if (forcee) return forcee
    return dets.filter(d => !d.invalide).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0]
  }

  function getDernierPoint(courseId) {
    const pts = coursePoints.filter(cp => cp.course_id === courseId).sort((a, b) => b.ordre - a.ordre)
    return pts[0]
  }

  function getPointsOrdonnees(courseId) {
    return coursePoints.filter(cp => cp.course_id === courseId).sort((a, b) => a.ordre - b.ordre)
  }

  if (loading) return <div className="loading">Chargement...</div>

  // ===================== CALCULS SUIVI =====================
  function calcCourse(course) {
    const coureursCourse = coureurs.filter(c => c.course_id === course.id)
    const inscrits = coureursCourse.length
    const dossardsRetires = retraits.filter(r => coureursCourse.some(c => c.id === r.coureur_id)).length
    const dns = coureursCourse.filter(c => c.statut === 'DNS').length
    const dnf = coureursCourse.filter(c => c.statut === 'DNF').length
    const partants = dossardsRetires - dns
    const dernierPt = getDernierPoint(course.id)
    const finishers = dernierPt
      ? coureursCourse.filter(c => c.statut !== 'DNS' && !!getDetectionRetenue(c.id, dernierPt.point_chrono_id)).length
      : 0
    const enCourse = Math.max(0, partants - dnf - finishers)
    return { inscrits, dossardsRetires, dns, dnf, partants, finishers, enCourse, coureursCourse }
  }

  // ===================== CALCULS STATS =====================
  const coureursFiltresStats = filterStatsCourse
    ? coureurs.filter(c => c.course_id === filterStatsCourse)
    : coureurs

  const totalStats = coureursFiltresStats.length
  const nbVTT = coureursFiltresStats.filter(c => c.type_vtt === 'VTT').length
  const nbVTTAE = coureursFiltresStats.filter(c => c.type_vtt === 'VTTAE').length
  const nbFemmes = coureursFiltresStats.filter(c => c.genre === 'Feminin').length
  const agesMoyens = coureursFiltresStats.filter(c => c.age).map(c => c.age)
  const ageMoyen = agesMoyens.length > 0 ? Math.round(agesMoyens.reduce((a, b) => a + b, 0) / agesMoyens.length) : null

  function isLocal(cp) {
    if (!cp) return false
    const s = String(cp).trim()
    const dep = s.substring(0, 2)
    if (granularite === 'region') return AURA_DEPS.includes(dep)
    if (granularite === 'departement') return dep === depRef
    return s.substring(0, 5) === cpRef
  }

  const nbLocal = coureursFiltresStats.filter(c => isLocal(c.cp)).length
  const pctLocal = totalStats > 0 ? Math.round(nbLocal / totalStats * 100) : 0

  const localLabel = granularite === 'region' ? 'AURA' : granularite === 'departement' ? 'Dept ' + depRef : 'CP ' + cpRef

  // geoData : cle selon granularite
  const geoData = {}
  coureursFiltresStats.forEach(c => {
    if (!c.cp) return
    const s = String(c.cp).trim()
    const dep = s.substring(0, 2)
    let key
    if (granularite === 'region') key = DEP_TO_REGION[dep] || 'Autre'
    else if (granularite === 'departement') key = dep
    else key = s.substring(0, 5)
    if (key) geoData[key] = (geoData[key] || 0) + 1
  })

  const geoSorted = Object.entries(geoData).sort((a, b) => b[1] - a[1]).slice(0, 15)

  // Distribution ages
  const tranches = ['<20', '20-29', '30-39', '40-49', '50-59', '60+']
  const distAges = tranches.map(t => {
    const inTranche = coureursFiltresStats.filter(c => {
      if (!c.age) return false
      if (t === '<20') return c.age < 20
      if (t === '60+') return c.age >= 60
      const [min, max] = t.split('-').map(Number)
      return c.age >= min && c.age <= max
    })
    return {
      tranche: t,
      VTT: inTranche.filter(c => c.type_vtt === 'VTT').length,
      VTTAE: inTranche.filter(c => c.type_vtt === 'VTTAE').length,
      total: inTranche.length
    }
  })

  // Flux passages
  function getFluxData() {
    let dets = []
    if (!filterStatsCourse) {
      const derniersPoints = courses.map(co => getDernierPoint(co.id)).filter(Boolean).map(p => p.point_chrono_id)
      dets = detections.filter(d => !d.invalide && derniersPoints.includes(d.point_chrono_id))
    } else {
      const pointId = fluxPoint || getDernierPoint(filterStatsCourse)?.point_chrono_id
      if (!pointId) return []
      dets = detections.filter(d => !d.invalide && d.point_chrono_id === pointId)
    }
    const buckets = {}
    dets.forEach(d => {
      const dt = new Date(d.timestamp)
      const h = dt.getHours()
      const m = Math.floor(dt.getMinutes() / 15) * 15
      const key = String(h).padStart(2,'0') + ':' + String(m).padStart(2,'0')
      buckets[key] = (buckets[key] || 0) + 1
    })
    return Object.entries(buckets).sort((a, b) => a[0].localeCompare(b[0])).map(([heure, passages]) => ({ heure, passages }))
  }

  const fluxData = getFluxData()
  const fluxPic = fluxData.length > 0 ? fluxData.reduce((a, b) => b.passages > a.passages ? b : a) : null
  const pointsCourseSelectionnee = filterStatsCourse
    ? getPointsOrdonnees(filterStatsCourse).map(cp => points.find(p => p.id === cp.point_chrono_id)).filter(Boolean)
    : []

  // ===================== COURBES EVOLUTION J-60 =====================
  function buildEvolutionData() {
    const j0 = dateJ0 ? new Date(dateJ0) : null
    if (!j0 || isNaN(j0)) return []

    // On prend les coureurs avec date_commande dans la fenetre J-60 → J0
    const j60 = new Date(j0)
    j60.setDate(j60.getDate() - 60)

    // Filtrer selon la course selectionnee
    const base = filterStatsCourse
      ? coureurs.filter(c => c.course_id === filterStatsCourse)
      : coureurs

    const avecDate = base.filter(c => c.date_commande)
    const sansDate = base.filter(c => !c.date_commande).length

    // Construire le tableau jour par jour J-60 → J0
    const days = []
    for (let i = 0; i <= 60; i++) {
      const d = new Date(j60)
      d.setDate(j60.getDate() + i)
      const dStr = d.toISOString().slice(0, 10)
      const label = 'J-' + (60 - i)

      const inscrits = avecDate.filter(c => c.date_commande.slice(0, 10) <= dStr).length
      const repas = avecDate
        .filter(c => c.date_commande.slice(0, 10) <= dStr)
        .reduce((sum, c) => sum + (c.nb_repas || 0), 0)

      days.push({ label, date: dStr, inscrits, repas })
    }
    return { days, sansDate }
  }

  const evolutionResult = buildEvolutionData()
  const evolutionDays = evolutionResult?.days || []
  const evolutionSansDate = evolutionResult?.sansDate || 0

  // isLocalGeo pour la liste geo
  function isLocalGeo(key) {
    if (granularite === 'region') return key === 'AURA'
    if (granularite === 'departement') return key === depRef
    return key === cpRef
  }

  return (
    <div className="dashboard">

      {/* ONGLETS */}
      <div className="dash-onglets">
        <button className={'dash-onglet ' + (onglet === 'suivi' ? 'active' : '')} onClick={() => setOnglet('suivi')}>
          Suivi temps reel
        </button>
        <button className={'dash-onglet ' + (onglet === 'stats' ? 'active' : '')} onClick={() => setOnglet('stats')}>
          Analyses
        </button>
        <button className={'dash-onglet ' + (onglet === 'evolution' ? 'active' : '')} onClick={() => setOnglet('evolution')}>
          Evolution inscriptions
        </button>
      </div>

      {/* ===================== ONGLET SUIVI ===================== */}
      {onglet === 'suivi' && (
        <div className="suivi">
          {courses.map(course => {
            const { inscrits, dossardsRetires, dns, dnf, partants, finishers, enCourse, coureursCourse } = calcCourse(course)
            const ptsOrd = getPointsOrdonnees(course.id)
            return (
              <div key={course.id} className="suivi-course">
                <div className="suivi-course-title">{course.nom}</div>
                <div className="suivi-kpis">
                  <div className="suivi-kpi">
                    <div className="suivi-kpi-val">{inscrits}</div>
                    <div className="suivi-kpi-label">Inscrits</div>
                  </div>
                  <div className="suivi-kpi">
                    <div className="suivi-kpi-val">{dossardsRetires}</div>
                    <div className="suivi-kpi-label">Dossards retires</div>
                  </div>
                  {dns > 0 && (
                    <div className="suivi-kpi dns">
                      <div className="suivi-kpi-val">{dns}</div>
                      <div className="suivi-kpi-label">DNS</div>
                    </div>
                  )}
                  <div className="suivi-kpi highlight">
                    <div className="suivi-kpi-val">{partants}</div>
                    <div className="suivi-kpi-label">Partants</div>
                  </div>
                  {dnf > 0 && (
                    <div className="suivi-kpi dnf">
                      <div className="suivi-kpi-val">{dnf}</div>
                      <div className="suivi-kpi-label">DNF</div>
                    </div>
                  )}
                  <div className="suivi-kpi en-course">
                    <div className="suivi-kpi-val">{enCourse}</div>
                    <div className="suivi-kpi-label">En course</div>
                  </div>
                  <div className="suivi-kpi success">
                    <div className="suivi-kpi-val">{finishers}</div>
                    <div className="suivi-kpi-label">Finishers</div>
                  </div>
                </div>

                <div className="suivi-points">
                  {ptsOrd.map(cp => {
                    const point = points.find(p => p.id === cp.point_chrono_id)
                    if (!point) return null
                    const attendus = coureursCourse.filter(c => c.statut !== 'DNS')
                    const detectes = attendus.filter(c => !!getDetectionRetenue(c.id, point.id))
                    const pct = attendus.length > 0 ? Math.round(detectes.length / attendus.length * 100) : 0
                    return (
                      <div key={cp.id} className="suivi-point">
                        <div className="suivi-point-header">
                          <div className="suivi-point-name">
                            <span className="suivi-point-ordre">{cp.ordre}</span>
                            {point.nom}
                          </div>
                          <div className="suivi-point-count">
                            <strong>{detectes.length}</strong>/{attendus.length}
                            <span className="suivi-point-pct">{pct}%</span>
                          </div>
                        </div>
                        <div className="suivi-progress">
                          <div className="suivi-progress-bar" style={{width: pct + '%'}}/>
                        </div>
                      </div>
                    )
                  })}
                  <div className="suivi-point attendus-arrivee">
                    <div className="suivi-point-header">
                      <div className="suivi-point-name">
                        <span className="suivi-point-ordre" style={{background:'#856404'}}>?</span>
                        Attendus a l'arrivee
                      </div>
                      <div className="suivi-point-count">
                        <strong>{Math.max(0, partants - dnf - finishers)}</strong>/{partants}
                      </div>
                    </div>
                    <div className="suivi-progress">
                      <div className="suivi-progress-bar" style={{width: partants > 0 ? (Math.max(0, partants - dnf - finishers) / partants * 100) + '%' : '0%', background:'#856404'}}/>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* ===================== ONGLET EVOLUTION ===================== */}
      {onglet === 'evolution' && (
        <div className="stats">
          {/* CONFIG */}
          <div className="stats-config">
            <div className="stats-config-group">
              <label>Date J0 (jour de la course)</label>
              <input type="date" value={dateJ0} onChange={e => setDateJ0(e.target.value)} style={{width:160}} />
            </div>
            <button className="btn success" onClick={saveConfig}>Sauvegarder</button>
            <div style={{marginLeft:'auto'}}>
              <select className="course-filter-select" value={filterStatsCourse} onChange={e => setFilterStatsCourse(e.target.value)}>
                <option value="">Toutes les courses</option>
                {courses.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
              </select>
            </div>
          </div>

          {!dateJ0 ? (
            <div style={{background:'white', borderRadius:8, padding:32, textAlign:'center', color:'#aaa', boxShadow:'0 1px 3px rgba(0,0,0,0.1)'}}>
              Veuillez renseigner la date J0 (jour de la course) pour afficher les courbes.
            </div>
          ) : (
            <>
              {evolutionSansDate > 0 && (
                <div style={{background:'#fff3cd', border:'1px solid #ffc107', borderRadius:6, padding:'8px 14px', fontSize:13, color:'#856404', marginBottom:12}}>
                  ⚠️ {evolutionSansDate} inscription{evolutionSansDate > 1 ? 's' : ''} sans date de commande — non affichée{evolutionSansDate > 1 ? 's' : ''} dans les courbes.
                </div>
              )}

              {/* COURBE INSCRITS CUMULES */}
              <div className="stats-card" style={{marginBottom:16}}>
                <div className="stats-card-header">
                  <h3>Inscriptions cumulées (J-60 → J0)</h3>
                  <span style={{fontSize:13, color:'#1a7a5e', fontWeight:700}}>
                    {evolutionDays.length > 0 ? evolutionDays[evolutionDays.length - 1].inscrits : 0} total
                  </span>
                </div>
                {evolutionDays.length === 0 ? (
                  <div style={{height:220, display:'flex', alignItems:'center', justifyContent:'center', color:'#aaa', fontSize:13}}>
                    Aucune donnée disponible
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={evolutionDays} margin={{top:5, right:10, bottom:5, left:0}}>
                      <XAxis
                        dataKey="label"
                        tick={{fontSize:10}}
                        interval={9}
                        tickFormatter={v => v}
                      />
                      <YAxis tick={{fontSize:11}} allowDecimals={false} />
                      <Tooltip
                        formatter={(value) => [value, 'Inscrits cumulés']}
                        labelFormatter={(label) => label}
                      />
                      <Area
                        type="monotone"
                        dataKey="inscrits"
                        stroke="#1a7a5e"
                        fill="#b2d8cc"
                        strokeWidth={2}
                        dot={false}
                        activeDot={{r:4}}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* COURBE REPAS CUMULES */}
              <div className="stats-card">
                <div className="stats-card-header">
                  <h3>Repas cumulés (J-60 → J0)</h3>
                  <span style={{fontSize:13, color:'#e67e22', fontWeight:700}}>
                    {evolutionDays.length > 0 ? evolutionDays[evolutionDays.length - 1].repas : 0} total
                  </span>
                </div>
                {evolutionDays.length === 0 ? (
                  <div style={{height:220, display:'flex', alignItems:'center', justifyContent:'center', color:'#aaa', fontSize:13}}>
                    Aucune donnée disponible
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={evolutionDays} margin={{top:5, right:10, bottom:5, left:0}}>
                      <XAxis
                        dataKey="label"
                        tick={{fontSize:10}}
                        interval={9}
                        tickFormatter={v => v}
                      />
                      <YAxis tick={{fontSize:11}} allowDecimals={false} />
                      <Tooltip
                        formatter={(value) => [value, 'Repas cumulés']}
                        labelFormatter={(label) => label}
                      />
                      <Area
                        type="monotone"
                        dataKey="repas"
                        stroke="#e67e22"
                        fill="#fde3c4"
                        strokeWidth={2}
                        dot={false}
                        activeDot={{r:4}}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* ===================== ONGLET STATS ===================== */}
      {onglet === 'stats' && (
        <div className="stats">

          {/* CONFIG + FILTRE */}
          <div className="stats-config">
            <div className="stats-config-group">
              <label>Region ref</label>
              <input value={regionRef} onChange={e => setRegionRef(e.target.value)} placeholder="ex: Auvergne-Rhone-Alpes" />
            </div>
            <div className="stats-config-group">
              <label>Dept ref</label>
              <input value={depRef} onChange={e => setDepRef(e.target.value)} placeholder="ex: 73" style={{width:60}} />
            </div>
            <div className="stats-config-group">
              <label>CP ref</label>
              <input value={cpRef} onChange={e => setCpRef(e.target.value)} placeholder="ex: 73190" style={{width:80}} />
            </div>
            <div className="stats-config-group">
              <label>Date J0</label>
              <input type="date" value={dateJ0} onChange={e => setDateJ0(e.target.value)} style={{width:140}} />
            </div>
            <button className="btn success" onClick={saveConfig}>Sauvegarder</button>
            <div style={{marginLeft:'auto'}}>
              <select className="course-filter-select" value={filterStatsCourse} onChange={e => { setFilterStatsCourse(e.target.value); setFluxPoint(null) }}>
                <option value="">Toutes les courses</option>
                {courses.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
              </select>
            </div>
          </div>

          {/* KPIs */}
          <div className="stats-kpis">
            <div className="stat-card primary">
              <div className="stat-value">{totalStats}</div>
              <div className="stat-label">Coureurs</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{nbVTT}</div>
              <div className="stat-label">VTT</div>
              <div className="stat-sub">{totalStats > 0 ? Math.round(nbVTT/totalStats*100) : 0}%</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{nbVTTAE}</div>
              <div className="stat-label">VTTAE</div>
              <div className="stat-sub">{totalStats > 0 ? Math.round(nbVTTAE/totalStats*100) : 0}%</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{nbFemmes}</div>
              <div className="stat-label">Femmes</div>
              <div className="stat-sub">{totalStats > 0 ? Math.round(nbFemmes/totalStats*100) : 0}%</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{nbLocal}</div>
              <div className="stat-label">{localLabel}</div>
              <div className="stat-sub">{pctLocal}%</div>
            </div>
            {ageMoyen && (
              <div className="stat-card">
                <div className="stat-value">{ageMoyen}</div>
                <div className="stat-label">Age moyen</div>
              </div>
            )}
          </div>

          {/* GRILLE PRINCIPALE */}
          <div className="stats-grid-main">

            {/* GEO */}
            <div className="stats-card large">
              <div className="stats-card-header">
                <h3>Repartition geographique</h3>
                <div className="granularite-tabs">
                  {[['region','Region'],['departement','Departement'],['cp','Code postal']].map(([g, label]) => (
                    <button key={g} className={'gran-tab ' + (granularite === g ? 'active' : '')} onClick={() => setGranularite(g)}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="stats-geo-content">
                <div className="stats-map">
                  <FranceMap
  granularite={granularite}
  geoData={geoData}
  depRef={depRef}
  cpRef={cpRef}
  regionRef={regionRef}
  total={totalStats}
/>
                </div>
                <div className="stats-geo-list">
                  <div className="geo-legend">
                    <span className="geo-dot local"></span>
                    <span>{localLabel}</span>
                    <span className="geo-dot autre" style={{marginLeft:8}}></span>
                    <span>Autre</span>
                  </div>
                  {geoSorted.map(([key, count]) => (
                    <div key={key} className="geo-row">
                      <span className={'geo-bar-label ' + (isLocalGeo(key) ? 'local' : '')}>{key}</span>
                      <div className="geo-bar-wrap">
                        <div className="geo-bar" style={{
                          width: totalStats > 0 ? Math.max(4, count/totalStats*100*3) + 'px' : '0px',
                          background: isLocalGeo(key) ? '#e67e22' : '#3498db',
                          maxWidth:'120px'
                        }}/>
                      </div>
                      <span className="geo-count">{count}</span>
                      <span className="geo-pct">{totalStats > 0 ? Math.round(count/totalStats*100) : 0}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* AGES */}
            <div className="stats-card">
              <div className="stats-card-header"><h3>Repartition par age</h3></div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={distAges} margin={{top:5, right:10, bottom:5, left:0}}>
                  <XAxis dataKey="tranche" tick={{fontSize:11}} />
                  <YAxis tick={{fontSize:11}} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="VTT" fill="#3498db" />
                  <Bar dataKey="VTTAE" fill="#e67e22" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* TYPE VELO */}
            <div className="stats-card small">
              <div className="stats-card-header"><h3>Type de velo</h3></div>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={[{name:'VTT', value:nbVTT},{name:'VTTAE', value:nbVTTAE}]}
                    cx="50%" cy="50%" innerRadius={55} outerRadius={85}
                    dataKey="value"
                    label={({name, value}) => value > 0 ? name + ' ' + value : ''}
                  >
                    <Cell fill="#3498db"/>
                    <Cell fill="#e67e22"/>
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* GENRE */}
            <div className="stats-card small">
              <div className="stats-card-header"><h3>Genre</h3></div>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={[{name:'Masculin', value:totalStats-nbFemmes},{name:'Feminin', value:nbFemmes}]}
                    cx="50%" cy="50%" innerRadius={55} outerRadius={85}
                    dataKey="value"
                    label={({name, value}) => value > 0 ? name + ' ' + value : ''}
                  >
                    <Cell fill="#3498db"/>
                    <Cell fill="#e74c3c"/>
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* FLUX */}
            <div className="stats-card flux">
              <div className="stats-card-header">
                <h3>Flux de passages</h3>
                {fluxPic && <span className="flux-pic">Pic : {fluxPic.heure} ({fluxPic.passages})</span>}
              </div>
              {filterStatsCourse && pointsCourseSelectionnee.length > 0 && (
                <div className="flux-points-tabs">
                  {pointsCourseSelectionnee.map(p => {
                    const dernierPtId = getDernierPoint(filterStatsCourse)?.point_chrono_id
                    return (
                      <button
                        key={p.id}
                        className={'flux-tab ' + ((fluxPoint === p.id || (!fluxPoint && p.id === dernierPtId)) ? 'active' : '')}
                        onClick={() => setFluxPoint(p.id)}
                      >
                        {p.nom}
                      </button>
                    )
                  })}
                </div>
              )}
              {fluxData.length === 0 ? (
                <div style={{height:200, display:'flex', alignItems:'center', justifyContent:'center', color:'#aaa', fontSize:13}}>
                  Aucune donnee de passage disponible
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={fluxData} margin={{top:5, right:10, bottom:5, left:0}}>
                    <XAxis dataKey="heure" tick={{fontSize:10}} interval="preserveStartEnd" />
                    <YAxis tick={{fontSize:11}} />
                    <Tooltip />
                    <Area type="monotone" dataKey="passages" stroke="#1a7a5e" fill="#b2d8cc" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>

          </div>
        </div>
      )}
    </div>
  )
}
